import { useAtomValue } from 'jotai';
import { Dialog } from 'primereact/dialog';
import React, { useState } from 'react';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    ChevronDownIcon,
    ChevronRightIcon,
    H5Emphasized,
    Icon,
    Input,
    Spinner,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { Button } from '../../common/components/GroveButtons';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { centsToFormattedDollars } from '../../common/utils/formatting';
import {
    apexAccountUrl,
    apexTransferUrl,
    syncteraPendingTransactionUrl,
    syncteraPostedTransactionUrl,
} from '../../common/utils/LinkUtils';
import { adminUserAtom } from '../../entities/entityAtoms';
import type { TaxWithholding } from '../../entities/membership/hooks/investments/useTestICTWithdrawal';
import { useCancelIctTransfer } from '../hooks/useIctData';
import type { AdminICTTransferDetailDto } from '../types/ImOpsTypes';
import { ICT_TERMINAL_STATUSES } from '../types/ImOpsTypes';

interface TransferDetailsModalProps {
    transfer: AdminICTTransferDetailDto | null;
    isOpen: boolean;
    onClose: () => void;
    onCancelSuccess: () => void;
}

/**
 * Formats one leg of a retirement distribution's withholding instruction. Apex accepts
 * either a percentage or a fixed amount, never both.
 */
const formatWithholdingInstruction = (
    withholding: TaxWithholding | undefined,
): string | null => {
    if (withholding?.percentage != null) {
        return `${withholding.percentage}%`;
    }

    if (withholding?.amountCents != null) {
        return centsToFormattedDollars(withholding.amountCents);
    }

    return null;
};

export const TransferDetailsModal = ({
    transfer,
    isOpen,
    onClose,
    onCancelSuccess,
}: TransferDetailsModalProps) => {
    const [cancelNote, setCancelNote] = useState('');
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [isStateLogExpanded, setIsStateLogExpanded] = useState(false);

    const adminUser = useAtomValue(adminUserAtom);
    const toast = useToastController();

    const cancelMutation = useCancelIctTransfer();

    if (transfer == null) {
        return null;
    }

    const handleCancelClick = () => {
        setShowCancelConfirm(true);
    };

    const handleConfirmCancel = async () => {
        try {
            const result = await cancelMutation.mutateAsync({
                body: {
                    idempotencyKey: transfer.idempotencyKey,
                    transferType: transfer.transferType,
                    cancellationReason: cancelNote,
                    adminUserEmail: adminUser.email,
                },
            });

            if (result.success) {
                toast.show(
                    result.message || 'Transfer cancelled successfully',
                    { ...MutationSuccessToastOptions, duration: 10000 },
                );
                setShowCancelConfirm(false);
                setCancelNote('');
                onClose();
                onCancelSuccess(); // Refresh the data
            } else {
                toast.show(result.message || 'Failed to cancel transfer', {
                    ...MutationErrorToastOptions(result.message),
                    duration: 10000,
                });
            }
        } catch (error) {
            toast.show('Error cancelling transfer', {
                ...MutationErrorToastOptions(error),
                duration: 10000,
            });
        }
    };

    const canCancel = !ICT_TERMINAL_STATUSES.includes(transfer.status);
    const isWithdrawal = transfer.transferType === 'WITHDRAWAL';

    // On withdrawals retirementDetails holds the retirement_distribution instructions
    // sent to Apex (deposits hold retirement_contribution, which has no withholding).
    const retirementDistribution = isWithdrawal
        ? transfer.retirementDetails
        : null;
    const federalWithholding = formatWithholdingInstruction(
        retirementDistribution?.federalTaxWithholding,
    );
    const stateWithholding = formatWithholdingInstruction(
        retirementDistribution?.stateTaxWithholding,
    );
    const isStateWithholdingWaived =
        retirementDistribution?.stateWithholdingWaiver ?? false;
    const hasRetirementWithholding =
        federalWithholding != null ||
        stateWithholding != null ||
        isStateWithholdingWaived;

    // Type guard for state log entries
    const isStateLogEntry = (
        entry: Record<string, unknown>,
    ): entry is {
        time: string;
        source: 'ORCHESTRATION' | 'APEX' | 'SYNCTERA_PAY';
        state: string;
        actor?: string;
        message?: string;
        metadata?: Record<string, unknown>;
    } => {
        return (
            typeof entry.time === 'string' &&
            typeof entry.source === 'string' &&
            typeof entry.state === 'string'
        );
    };

    const getSourceColor = (
        source: 'ORCHESTRATION' | 'APEX' | 'SYNCTERA_PAY',
    ): string => {
        switch (source) {
            case 'ORCHESTRATION':
                return '#2196F3'; // blue
            case 'APEX':
                return '#FF9800'; // orange
            case 'SYNCTERA_PAY':
                return '#9C27B0'; // purple
            default:
                return '#000000';
        }
    };

    const header = (
        <XStack justifyContent="space-between" alignItems="center" width="100%">
            <H5Emphasized>
                {transfer.transferType} Transfer Details
            </H5Emphasized>
        </XStack>
    );

    return (
        <Dialog
            header={header}
            visible={isOpen}
            style={{ width: '50vw' }}
            onHide={onClose}
            draggable={false}
            resizable={false}
        >
            <YStack gap={24} paddingVertical={16}>
                {/* Transfer Identification */}
                <YStack gap={8}>
                    <BodyLargeEmphasized>
                        Transfer Information
                    </BodyLargeEmphasized>
                    <YStack gap={0}>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                            borderBottomWidth={1}
                            borderBottomColor="$gray500"
                        >
                            <BodySmallRegular>
                                Idempotency Key:
                            </BodySmallRegular>
                            <BodySmallRegular fontFamily="monospace">
                                {transfer.idempotencyKey}
                            </BodySmallRegular>
                        </XStack>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                            borderBottomWidth={1}
                            borderBottomColor="$gray500"
                        >
                            <BodySmallRegular>Workflow ID:</BodySmallRegular>
                            <BodySmallRegular fontFamily="monospace">
                                {transfer.workflowId}
                            </BodySmallRegular>
                        </XStack>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                            borderBottomWidth={1}
                            borderBottomColor="$gray500"
                        >
                            <BodySmallRegular>
                                Settlement Date:
                            </BodySmallRegular>
                            <BodySmallRegular>
                                {transfer.settlementDate}
                            </BodySmallRegular>
                        </XStack>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                        >
                            <BodySmallRegular>Type:</BodySmallRegular>
                            <BodySmallRegular>
                                {transfer.transferType}
                            </BodySmallRegular>
                        </XStack>
                    </YStack>
                </YStack>

                {/* User/Account Information */}
                <YStack gap={8}>
                    <BodyLargeEmphasized>
                        Account Information
                    </BodyLargeEmphasized>
                    <YStack gap={0}>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                            borderBottomWidth={1}
                            borderBottomColor="$gray500"
                        >
                            <BodySmallRegular>Membership ID:</BodySmallRegular>
                            <BodySmallRegular>
                                {transfer.membershipId}
                            </BodySmallRegular>
                        </XStack>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                            borderBottomWidth={1}
                            borderBottomColor="$gray500"
                        >
                            <BodySmallRegular>User ID:</BodySmallRegular>
                            <BodySmallRegular>
                                {transfer.userId}
                            </BodySmallRegular>
                        </XStack>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                            borderBottomWidth={1}
                            borderBottomColor="$gray500"
                        >
                            <BodySmallRegular>
                                Bank Account ID:
                            </BodySmallRegular>
                            <BodySmallRegular>
                                {transfer.bankAccountId}
                            </BodySmallRegular>
                        </XStack>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                        >
                            <BodySmallRegular>
                                Apex Account ID:
                            </BodySmallRegular>
                            <a
                                href={apexAccountUrl(transfer.apexAccountId)}
                                target="_blank"
                                rel="noreferrer"
                                style={{ fontSize: '0.875rem' }}
                            >
                                {transfer.apexAccountId}
                            </a>
                        </XStack>
                    </YStack>
                </YStack>

                {/* Amount Information */}
                <YStack gap={8}>
                    <BodyLargeEmphasized>Amount Details</BodyLargeEmphasized>
                    <YStack gap={0}>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                            borderBottomWidth={1}
                            borderBottomColor="$gray500"
                        >
                            <BodySmallRegular>
                                {isWithdrawal ? 'Net Amount:' : 'Amount:'}
                            </BodySmallRegular>
                            <BodyMediumRegular fontWeight="bold">
                                {centsToFormattedDollars(
                                    transfer.amountCents ?? 0,
                                )}
                            </BodyMediumRegular>
                        </XStack>
                        {transfer.requestedAmountCents != null && (
                            <XStack
                                justifyContent="space-between"
                                paddingVertical={8}
                                borderBottomWidth={1}
                                borderBottomColor="$gray500"
                            >
                                <BodySmallRegular>
                                    Requested Amount (member gross):
                                </BodySmallRegular>
                                <BodySmallRegular>
                                    {centsToFormattedDollars(
                                        transfer.requestedAmountCents,
                                    )}
                                </BodySmallRegular>
                            </XStack>
                        )}
                        {transfer.actualAmountCents != null && (
                            <XStack
                                justifyContent="space-between"
                                paddingVertical={8}
                                borderBottomWidth={1}
                                borderBottomColor="$gray500"
                            >
                                <BodySmallRegular>
                                    Apex Distribution (gross):
                                </BodySmallRegular>
                                <BodySmallRegular>
                                    {centsToFormattedDollars(
                                        transfer.actualAmountCents,
                                    )}
                                </BodySmallRegular>
                            </XStack>
                        )}
                        {isWithdrawal && (
                            <XStack
                                justifyContent="space-between"
                                paddingVertical={8}
                                borderBottomWidth={1}
                                borderBottomColor="$gray500"
                            >
                                <BodySmallRegular>
                                    Recon Settlement (net cash credited):
                                </BodySmallRegular>
                                <BodySmallRegular>
                                    {transfer.settlementAmountCents != null
                                        ? centsToFormattedDollars(
                                              transfer.settlementAmountCents,
                                          )
                                        : 'Awaiting batch recon'}
                                </BodySmallRegular>
                            </XStack>
                        )}
                        {hasRetirementWithholding && (
                            <>
                                <XStack
                                    justifyContent="space-between"
                                    paddingVertical={8}
                                    borderBottomWidth={1}
                                    borderBottomColor="$gray500"
                                >
                                    <BodySmallRegular>
                                        Federal Withholding (requested):
                                    </BodySmallRegular>
                                    <BodySmallRegular>
                                        {federalWithholding ?? 'None'}
                                    </BodySmallRegular>
                                </XStack>
                                <XStack
                                    justifyContent="space-between"
                                    paddingVertical={8}
                                    borderBottomWidth={1}
                                    borderBottomColor="$gray500"
                                >
                                    <BodySmallRegular>
                                        State Withholding (requested):
                                    </BodySmallRegular>
                                    <BodySmallRegular>
                                        {stateWithholding ??
                                            (isStateWithholdingWaived
                                                ? 'Waived'
                                                : 'None')}
                                    </BodySmallRegular>
                                </XStack>
                            </>
                        )}
                        {transfer.taxAmountWithheldCents != null && (
                            <XStack
                                justifyContent="space-between"
                                paddingVertical={8}
                            >
                                <BodySmallRegular>
                                    Tax Withheld (actual):
                                </BodySmallRegular>
                                <BodySmallRegular>
                                    {centsToFormattedDollars(
                                        transfer.taxAmountWithheldCents,
                                    )}
                                </BodySmallRegular>
                            </XStack>
                        )}
                    </YStack>
                </YStack>

                {/* Status Information */}
                <YStack gap={8}>
                    <BodyLargeEmphasized>Status</BodyLargeEmphasized>
                    <YStack gap={0}>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                        >
                            <BodySmallRegular>Status:</BodySmallRegular>
                            <BodySmallRegular fontWeight="bold">
                                {transfer.status}
                            </BodySmallRegular>
                        </XStack>
                    </YStack>
                </YStack>

                {/* Apex Details */}
                <YStack gap={8}>
                    <BodyLargeEmphasized>Apex Details</BodyLargeEmphasized>
                    <YStack gap={0}>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                            borderBottomWidth={1}
                            borderBottomColor="$gray500"
                        >
                            <BodySmallRegular>Apex ICT ID:</BodySmallRegular>
                            {transfer.apexIctId != null ? (
                                <a
                                    href={apexTransferUrl(
                                        transfer.apexAccountId,
                                        transfer.apexIctId,
                                    )}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ fontSize: '0.875rem' }}
                                >
                                    {transfer.apexIctId}
                                </a>
                            ) : (
                                <BodySmallRegular>-</BodySmallRegular>
                            )}
                        </XStack>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                            borderBottomWidth={1}
                            borderBottomColor="$gray500"
                        >
                            <BodySmallRegular>Apex State:</BodySmallRegular>
                            <BodySmallRegular>
                                {transfer.apexCurrentState ?? '-'}
                            </BodySmallRegular>
                        </XStack>
                        {transfer.apexPostedAt && (
                            <XStack
                                justifyContent="space-between"
                                paddingVertical={8}
                                borderBottomWidth={1}
                                borderBottomColor="$gray500"
                            >
                                <BodySmallRegular>
                                    Apex Posted:
                                </BodySmallRegular>
                                <BodySmallRegular>
                                    {new Date(
                                        transfer.apexPostedAt,
                                    ).toLocaleString()}
                                </BodySmallRegular>
                            </XStack>
                        )}
                        {transfer.apexCompletedAt && (
                            <XStack
                                justifyContent="space-between"
                                paddingVertical={8}
                            >
                                <BodySmallRegular>
                                    Apex Completed:
                                </BodySmallRegular>
                                <BodySmallRegular>
                                    {new Date(
                                        transfer.apexCompletedAt,
                                    ).toLocaleString()}
                                </BodySmallRegular>
                            </XStack>
                        )}
                    </YStack>
                </YStack>

                {/* Synctera Details */}
                <YStack gap={8}>
                    <BodyLargeEmphasized>Synctera Details</BodyLargeEmphasized>
                    <YStack gap={0}>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                            borderBottomWidth={1}
                            borderBottomColor="$gray500"
                        >
                            <BodySmallRegular>Pay ID:</BodySmallRegular>
                            <BodySmallRegular>
                                {transfer.syncteraPayId ?? '-'}
                            </BodySmallRegular>
                        </XStack>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                            borderBottomWidth={1}
                            borderBottomColor="$gray500"
                        >
                            <BodySmallRegular>Pay State:</BodySmallRegular>
                            <BodySmallRegular>
                                {transfer.syncteraPayCurrentState ?? '-'}
                            </BodySmallRegular>
                        </XStack>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                            borderBottomWidth={1}
                            borderBottomColor="$gray500"
                        >
                            <BodySmallRegular>
                                Transaction UUID:
                            </BodySmallRegular>
                            {transfer.syncteraTransactionUuid != null ? (
                                <a
                                    href={
                                        transfer.syncteraPayPostedAt
                                            ? syncteraPostedTransactionUrl(
                                                  transfer.syncteraTransactionUuid,
                                              )
                                            : syncteraPendingTransactionUrl(
                                                  transfer.syncteraTransactionUuid,
                                              )
                                    }
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{
                                        fontSize: '0.875rem',
                                        fontFamily: 'monospace',
                                    }}
                                >
                                    {transfer.syncteraTransactionUuid}
                                </a>
                            ) : (
                                <BodySmallRegular fontFamily="monospace">
                                    -
                                </BodySmallRegular>
                            )}
                        </XStack>
                        {transfer.syncteraPayPendingCreatedAt && (
                            <XStack
                                justifyContent="space-between"
                                paddingVertical={8}
                                borderBottomWidth={1}
                                borderBottomColor="$gray500"
                            >
                                <BodySmallRegular>
                                    Pending Created:
                                </BodySmallRegular>
                                <BodySmallRegular>
                                    {new Date(
                                        transfer.syncteraPayPendingCreatedAt,
                                    ).toLocaleString()}
                                </BodySmallRegular>
                            </XStack>
                        )}
                        {transfer.syncteraPayPostedAt && (
                            <XStack
                                justifyContent="space-between"
                                paddingVertical={8}
                                borderBottomWidth={1}
                                borderBottomColor="$gray500"
                            >
                                <BodySmallRegular>Posted At:</BodySmallRegular>
                                <BodySmallRegular>
                                    {new Date(
                                        transfer.syncteraPayPostedAt,
                                    ).toLocaleString()}
                                </BodySmallRegular>
                            </XStack>
                        )}
                        {transfer.syncteraPostedDate != null && (
                            <XStack
                                justifyContent="space-between"
                                paddingVertical={8}
                                borderBottomWidth={1}
                                borderBottomColor="$gray500"
                            >
                                <BodySmallRegular>
                                    Posted Date:
                                </BodySmallRegular>
                                <BodySmallRegular>
                                    {transfer.syncteraPostedDate}
                                </BodySmallRegular>
                            </XStack>
                        )}
                        {transfer.syncteraPayEffectiveDate != null && (
                            <XStack
                                justifyContent="space-between"
                                paddingVertical={8}
                            >
                                <BodySmallRegular>
                                    Effective Date:
                                </BodySmallRegular>
                                <BodySmallRegular>
                                    {transfer.syncteraPayEffectiveDate}
                                </BodySmallRegular>
                            </XStack>
                        )}
                    </YStack>
                </YStack>

                {/* Timestamps */}
                <YStack gap={8}>
                    <BodyLargeEmphasized>Timestamps</BodyLargeEmphasized>
                    <YStack gap={0}>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                            borderBottomWidth={1}
                            borderBottomColor="$gray500"
                        >
                            <BodySmallRegular>Created:</BodySmallRegular>
                            <BodySmallRegular>
                                {new Date(transfer.createdAt).toLocaleString()}
                            </BodySmallRegular>
                        </XStack>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                            borderBottomWidth={1}
                            borderBottomColor="$gray500"
                        >
                            <BodySmallRegular>Updated:</BodySmallRegular>
                            <BodySmallRegular>
                                {new Date(transfer.updatedAt).toLocaleString()}
                            </BodySmallRegular>
                        </XStack>
                        {transfer.completedAt && (
                            <XStack
                                justifyContent="space-between"
                                paddingVertical={8}
                            >
                                <BodySmallRegular>Completed:</BodySmallRegular>
                                <BodySmallRegular>
                                    {new Date(
                                        transfer.completedAt,
                                    ).toLocaleString()}
                                </BodySmallRegular>
                            </XStack>
                        )}
                    </YStack>
                </YStack>

                {/* Settlement & Reconciliation */}
                <YStack gap={8}>
                    <BodyLargeEmphasized>
                        Settlement & Reconciliation
                    </BodyLargeEmphasized>
                    <YStack gap={0}>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                            borderBottomWidth={1}
                            borderBottomColor="$gray500"
                        >
                            <BodySmallRegular>
                                Settlement Date:
                            </BodySmallRegular>
                            <BodySmallRegular>
                                {transfer.settlementDate}
                            </BodySmallRegular>
                        </XStack>
                        {transfer.expectedSettlementDate != null && (
                            <XStack
                                justifyContent="space-between"
                                paddingVertical={8}
                                borderBottomWidth={1}
                                borderBottomColor="$gray500"
                            >
                                <BodySmallRegular>
                                    Expected Settlement:
                                </BodySmallRegular>
                                <BodySmallRegular>
                                    {transfer.expectedSettlementDate}
                                </BodySmallRegular>
                            </XStack>
                        )}
                        {transfer.reconSettlementDate != null && (
                            <XStack
                                justifyContent="space-between"
                                paddingVertical={8}
                                borderBottomWidth={1}
                                borderBottomColor="$gray500"
                            >
                                <BodySmallRegular>
                                    Recon Settlement Date:
                                </BodySmallRegular>
                                <BodySmallRegular>
                                    {transfer.reconSettlementDate}
                                </BodySmallRegular>
                            </XStack>
                        )}
                        {transfer.reconciledAt && (
                            <XStack
                                justifyContent="space-between"
                                paddingVertical={8}
                            >
                                <BodySmallRegular>
                                    Reconciled At:
                                </BodySmallRegular>
                                <BodySmallRegular>
                                    {new Date(
                                        transfer.reconciledAt,
                                    ).toLocaleString()}
                                </BodySmallRegular>
                            </XStack>
                        )}
                    </YStack>
                </YStack>

                {/* Memo */}
                {transfer.memo != null && (
                    <YStack gap={8}>
                        <BodyLargeEmphasized>Memo</BodyLargeEmphasized>
                        <BodySmallRegular>{transfer.memo}</BodySmallRegular>
                    </YStack>
                )}

                {/* State Log - Collapsible */}
                {transfer.stateLog != null && transfer.stateLog.length > 0 && (
                    <YStack gap={8}>
                        <XStack
                            gap={8}
                            alignItems="center"
                            cursor="pointer"
                            onPress={() => {
                                setIsStateLogExpanded(!isStateLogExpanded);
                            }}
                            pressStyle={{ opacity: 0.7 }}
                        >
                            <Icon
                                icon={
                                    isStateLogExpanded
                                        ? ChevronDownIcon
                                        : ChevronRightIcon
                                }
                                size={16}
                                color="$black"
                            />
                            <BodyLargeEmphasized>
                                State Log ({transfer.stateLog.length} entries)
                            </BodyLargeEmphasized>
                        </XStack>

                        {isStateLogExpanded && (
                            <YStack
                                gap={8}
                                backgroundColor="$gray100"
                                padding={12}
                                borderRadius={8}
                                maxHeight={400}
                                overflow="scroll"
                            >
                                {transfer.stateLog
                                    .filter(isStateLogEntry)
                                    .slice()
                                    .sort(
                                        (a, b) =>
                                            new Date(b.time).getTime() -
                                            new Date(a.time).getTime(),
                                    )
                                    .map((entry, index) => (
                                        <YStack
                                            key={index}
                                            gap={4}
                                            padding={8}
                                            backgroundColor="white"
                                            borderRadius={4}
                                            borderWidth={1}
                                            borderColor="$gray200"
                                        >
                                            <XStack
                                                justifyContent="space-between"
                                                alignItems="center"
                                            >
                                                <BodySmallRegular
                                                    fontWeight="bold"
                                                    color={getSourceColor(
                                                        entry.source,
                                                    )}
                                                >
                                                    {entry.source}
                                                </BodySmallRegular>
                                                <BodySmallRegular
                                                    fontSize={10}
                                                    color="$gray1000"
                                                >
                                                    {new Date(
                                                        entry.time,
                                                    ).toLocaleString()}
                                                </BodySmallRegular>
                                            </XStack>
                                            <XStack justifyContent="space-between">
                                                <BodySmallRegular>
                                                    State:
                                                </BodySmallRegular>
                                                <BodySmallRegular
                                                    fontWeight="bold"
                                                    fontFamily="monospace"
                                                >
                                                    {entry.state}
                                                </BodySmallRegular>
                                            </XStack>
                                            {entry.actor != null && (
                                                <XStack justifyContent="space-between">
                                                    <BodySmallRegular>
                                                        Actor:
                                                    </BodySmallRegular>
                                                    <BodySmallRegular>
                                                        {entry.actor}
                                                    </BodySmallRegular>
                                                </XStack>
                                            )}
                                            {entry.message != null && (
                                                <YStack gap={2}>
                                                    <BodySmallRegular fontWeight="bold">
                                                        Message:
                                                    </BodySmallRegular>
                                                    <BodySmallRegular
                                                        fontSize={11}
                                                        color="$gray1100"
                                                    >
                                                        {entry.message}
                                                    </BodySmallRegular>
                                                </YStack>
                                            )}
                                            {entry.metadata != null &&
                                                Object.keys(entry.metadata)
                                                    .length > 0 && (
                                                    <YStack gap={2}>
                                                        <BodySmallRegular fontWeight="bold">
                                                            Metadata:
                                                        </BodySmallRegular>
                                                        <BodySmallRegular
                                                            fontSize={10}
                                                            fontFamily="monospace"
                                                            color="$gray1100"
                                                        >
                                                            {JSON.stringify(
                                                                entry.metadata,
                                                                null,
                                                                2,
                                                            )}
                                                        </BodySmallRegular>
                                                    </YStack>
                                                )}
                                        </YStack>
                                    ))}
                            </YStack>
                        )}
                    </YStack>
                )}

                {/* Cancel Section */}
                {canCancel && !showCancelConfirm && (
                    <YStack gap={8}>
                        <Button
                            variant="secondary"
                            tone="danger"
                            onPress={handleCancelClick}
                        >
                            Cancel Transfer
                        </Button>
                    </YStack>
                )}

                {/* Cancel Confirmation */}
                {showCancelConfirm && (
                    <YStack
                        gap={12}
                        backgroundColor="$red200"
                        padding={16}
                        borderRadius={8}
                    >
                        <BodyLargeEmphasized color="$red1100">
                            Cancel Transfer?
                        </BodyLargeEmphasized>
                        <BodySmallRegular>
                            Enter a note explaining why this transfer is being
                            cancelled:
                        </BodySmallRegular>
                        <Input
                            placeholder="Cancellation reason..."
                            value={cancelNote}
                            onChangeText={setCancelNote}
                        />
                        {cancelMutation.isPending && (
                            <XStack justifyContent="center">
                                <Spinner />
                            </XStack>
                        )}
                        <XStack gap={12}>
                            <Button
                                variant="primary"
                                tone="danger"
                                onPress={() => {
                                    void handleConfirmCancel();
                                }}
                                disabled={
                                    cancelNote.trim().length === 0 ||
                                    cancelMutation.isPending
                                }
                            >
                                Confirm Cancel
                            </Button>
                            <Button
                                variant="secondary"
                                onPress={() => {
                                    setShowCancelConfirm(false);
                                    setCancelNote('');
                                }}
                                disabled={cancelMutation.isPending}
                            >
                                Keep Transfer
                            </Button>
                        </XStack>
                    </YStack>
                )}
            </YStack>
        </Dialog>
    );
};
